import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, Trash2, Printer, ArrowLeft } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PrintPreview } from "@/components/PrintDoc";
import { PharmacyInvoiceDoc } from "@/components/PharmacyInvoice";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy, titleize, today } from "@/lib/format";

const ACCOUNT_TYPES = ["hospital", "clinic", "nursing_home", "doctor", "business", "other"];
const money = (v) => Number(v || 0);
const baseUnit = (m) => m?.packaging?.[0]?.code || "unit";
const emptyAccount = () => ({ name: "", account_type: "hospital", contact_person: "", phone: "",
  email: "", gstin: "", drug_licence: "", address: "", city: "", state: "", pincode: "",
  credit_limit: "0", opening_balance: "0", notes: "" });

export default function B2B() {
  const { can, user } = useAuth();
  const isAdmin = ["business_admin", "pharmacy_admin", "platform_owner"].includes(user?.role);
  const canManage = can("pharmacy.b2b.manage");
  const canSettle = can("pharmacy.b2b.settle");
  const [accounts, setAccounts] = useState(null);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAccount());
  const [formError, setFormError] = useState("");
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setAccounts(null);
    try {
      const { data } = await api.get("/pharmacy/b2b/accounts", { params: { q: q || undefined } });
      setAccounts(data.items);
    } catch (e) { setError(apiError(e)); }
  }, [q]);

  useEffect(() => {
    if (!can("pharmacy.b2b.view")) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, can]);

  if (!can("pharmacy.b2b.view")) return <NoAccess what="B2B credit accounts" />;

  const submit = async (e) => {
    e.preventDefault(); setFormError("");
    const body = { ...form, credit_limit: money(form.credit_limit), opening_balance: money(form.opening_balance) };
    try {
      if (editing) await api.patch(`/pharmacy/b2b/accounts/${editing}`, body);
      else await api.post("/pharmacy/b2b/accounts", body);
      toast.success(editing ? "B2B account updated." : "B2B account created.");
      setOpen(false); setEditing(null); setForm(emptyAccount()); load();
    } catch (err) { setFormError(apiError(err)); }
  };

  if (detailId) {
    return <AccountDetail accountId={detailId} onBack={() => { setDetailId(null); load(); }}
      canManage={canManage} canSettle={canSettle} isAdmin={isAdmin} />;
  }

  return (
    <div data-testid="b2b-page">
      <PageHeader overline="Pharmacy" title="B2B credit accounts"
        subtitle="Hospitals, clinics and businesses supplied on credit. Each day's supply builds one open invoice (IST) until an admin settles the day."
        actions={canManage && (
          <button onClick={() => { setEditing(null); setForm(emptyAccount()); setFormError(""); setOpen(true); }}
            className={primaryBtnCls} data-testid="b2b-add-account-btn">Add B2B account</button>
        )} />

      <input placeholder="Search name, mobile, GSTIN or city" value={q} data-testid="b2b-search-input"
        onChange={(e) => setQ(e.target.value)} className={`${inputCls} mb-6 sm:!w-96`} />

      {error && <ErrorState message={error} />}
      {!accounts ? <Loading /> : (
        <Table testid="b2b-accounts-table"
          columns={["Account", "Contact", "Outstanding", "Credit available", "Status", ""]}
          rows={accounts}
          empty={<EmptyState title="No B2B accounts yet" subtitle="Add a hospital or business to supply on credit." testid="b2b-empty" />}
          renderRow={(a) => (
            <tr key={a.account_id} data-testid={`b2b-row-${a.account_id}`}>
              <Td className="font-medium text-slate-900">{a.name}
                <span className="block text-xs text-slate-400">{titleize(a.account_type)}{a.city ? ` · ${a.city}` : ""}</span>
              </Td>
              <Td className="text-slate-600">{a.contact_person || "—"}<span className="block text-xs text-slate-400">{a.phone || "—"}</span></Td>
              <Td className="font-semibold text-slate-900" data-testid={`b2b-outstanding-${a.account_id}`}>{inr(a.ledger?.outstanding || 0)}</Td>
              <Td className="text-slate-600">{a.credit_limit ? inr(a.ledger?.credit_available || 0) : "—"}</Td>
              <Td><span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                (a.status || "active") === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {(a.status || "active") === "active" ? "Active" : "Inactive"}</span></Td>
              <Td>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setDetailId(a.account_id)} className={primaryBtnCls} data-testid={`b2b-open-${a.account_id}`}>Open</button>
                  {canManage && (
                    <button onClick={() => { setEditing(a.account_id); setForm({ ...emptyAccount(), ...a,
                      credit_limit: String(a.credit_limit ?? 0), opening_balance: String(a.opening_balance ?? 0) });
                      setFormError(""); setOpen(true); }} className={ghostBtnCls} data-testid={`b2b-edit-${a.account_id}`}>Edit</button>
                  )}
                </div>
              </Td>
            </tr>
          )} />
      )}

      <Modal open={open} onClose={() => { setOpen(false); setEditing(null); }} wide
        title={editing ? "Edit B2B account" : "Add B2B account"} testid="b2b-account-modal">
        <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2">
          <Field label="Account name *"><input required minLength={2} className={inputCls} value={form.name}
            data-testid="b2b-name-input" onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Account type">
            <select className={inputCls} value={form.account_type} data-testid="b2b-type-select"
              onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
              {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{titleize(t)}</option>)}
            </select>
          </Field>
          <Field label="Contact person"><input className={inputCls} value={form.contact_person}
            onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
          <Field label="Mobile"><input className={inputCls} value={form.phone} data-testid="b2b-phone-input"
            onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Email"><input className={inputCls} value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="GSTIN"><input className={inputCls} value={form.gstin}
            onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></Field>
          <Field label="Drug licence"><input className={inputCls} value={form.drug_licence}
            onChange={(e) => setForm({ ...form, drug_licence: e.target.value })} /></Field>
          <Field label="Address"><input className={inputCls} value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          <Field label="City"><input className={inputCls} value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
          <Field label="State"><input className={inputCls} value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
          <Field label="PIN code"><input className={inputCls} value={form.pincode}
            onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></Field>
          <Field label="Credit limit (₹)"><input type="number" min="0" className={inputCls} value={form.credit_limit}
            data-testid="b2b-credit-limit-input" onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} /></Field>
          <Field label="Opening balance (₹)"><input type="number" className={inputCls} value={form.opening_balance}
            onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} /></Field>
          <Field label="Notes" className="sm:col-span-2"><input className={inputCls} value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          {formError && <p className="sm:col-span-2 text-sm text-red-600" data-testid="b2b-form-error">{formError}</p>}
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="b2b-account-submit-btn">
            {editing ? "Update account" : "Create account"}</button></div>
        </form>
      </Modal>
    </div>
  );
}

const TABS = [["today", "Today's invoice"], ["invoices", "All invoices"], ["payments", "Payments"],
  ["statement", "Monthly statement"], ["items", "Item history"]];

function AccountDetail({ accountId, onBack, canManage, canSettle, isAdmin }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("today");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState("1");
  const [saleUnit, setSaleUnit] = useState("unit");
  const [lineDisc, setLineDisc] = useState("0");
  const [cart, setCart] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editInv, setEditInv] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [editReason, setEditReason] = useState("");
  const [closeInv, setCloseInv] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payment, setPayment] = useState({ amount: "", mode: "bank_transfer", reference: "", paid_on: today(), notes: "" });
  const [month, setMonth] = useState(today().slice(0, 7));
  const [statement, setStatement] = useState(null);
  const [itemHistory, setItemHistory] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/pharmacy/b2b/accounts/${accountId}`);
      setData(data);
    } catch (e) { toast.error(apiError(e)); onBack(); }
  }, [accountId, onBack]);

  useEffect(() => { load(); }, [load]);

  const search = async (value) => {
    setQ(value); setPicked(null);
    if (value.trim().length < 2) return setResults([]);
    try {
      const { data } = await api.get("/pharmacy/medicines/search", { params: { q: value.trim() } });
      setResults(data.items || []);
    } catch { setResults([]); }
  };

  const sellFactor = (m, unit) => Number((m?.packaging || []).find((e) => e.code === unit)?.factor || 1);

  const addToCart = () => {
    if (!picked) { toast.error("Search and select a product first."); return; }
    const n = Number(qty || 0);
    if (n <= 0) { toast.error("Quantity must be at least 1."); return; }
    const batch = picked.batches?.[0];
    if (!batch) { toast.error(`${picked.name} has no sellable stock.`); return; }
    const unit = saleUnit || baseUnit(picked);
    const factor = sellFactor(picked, unit);
    if (n * factor > batch.quantity) { toast.error(`Only ${Math.floor(batch.quantity / factor)} ${unit}(s) available.`); return; }
    const rate = (batch.selling_price || batch.mrp) * factor;
    setCart([...cart, { batch_id: batch.batch_id, batch_no: batch.batch_no, name: picked.name,
      sale_unit: unit, quantity: n, rate, discount_percent: money(lineDisc),
      gst_percent: batch.gst_percent }]);
    setPicked(null); setQ(""); setResults([]); setQty("1"); setSaleUnit("unit"); setLineDisc("0");
  };

  const addToInvoice = async () => {
    if (!cart.length) { toast.error("Add at least one product."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/pharmacy/b2b/accounts/${accountId}/add-items`, {
        items: cart.map((l) => ({ batch_id: l.batch_id, quantity: l.quantity, sale_unit: l.sale_unit,
          discount_percent: l.discount_percent, discount_amount: 0 })) });
      toast.success(`Added to ${data.invoice.invoice_no}.`);
      setCart([]); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const openEdit = (inv) => {
    setEditInv(inv); setEditReason("");
    setEditLines(inv.items.map((l) => ({ batch_id: l.batch_id, name: l.medicine_name, batch_no: l.batch_no,
      sale_unit: l.sale_unit, quantity: String(l.quantity), discount_percent: String(l.discount_percent || 0) })));
  };

  const submitEdit = async () => {
    const items = editLines.filter((l) => Number(l.quantity) > 0).map((l) => ({
      batch_id: l.batch_id, quantity: Number(l.quantity), sale_unit: l.sale_unit,
      discount_percent: money(l.discount_percent), discount_amount: 0 }));
    if (!items.length) { toast.error("Keep at least one item."); return; }
    if (editReason.trim().length < 3) { toast.error("A reason is required."); return; }
    try {
      await api.patch(`/pharmacy/b2b/invoices/${editInv.invoice_id}`, {
        items, discount_mode: "amount", overall_discount: 0, reason: editReason.trim() });
      toast.success("Invoice corrected. Stock adjusted and change audited.");
      setEditInv(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doClose = async () => {
    try {
      await api.post(`/pharmacy/b2b/invoices/${closeInv.invoice_id}/close`, {
        discount_mode: "amount", overall_discount: 0, notes: "Day settled" });
      toast.success("Daily invoice closed and locked.");
      setCloseInv(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/pharmacy/b2b/accounts/${accountId}/payments`,
        { ...payment, amount: money(payment.amount) });
      toast.success("Settlement recorded.");
      setPayOpen(false); setPayment({ amount: "", mode: "bank_transfer", reference: "", paid_on: today(), notes: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const loadStatement = useCallback(async () => {
    try {
      const { data } = await api.get(`/pharmacy/b2b/accounts/${accountId}/statement`, { params: { month } });
      setStatement(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [accountId, month]);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get(`/pharmacy/b2b/accounts/${accountId}/items`);
      setItemHistory(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [accountId]);

  useEffect(() => { if (tab === "statement") loadStatement(); }, [tab, loadStatement]);
  useEffect(() => { if (tab === "items") loadItems(); }, [tab, loadItems]);

  const printInvoice = async (invoiceId) => {
    try {
      const { data } = await api.get(`/pharmacy/b2b/invoices/${invoiceId}`);
      const inv = data.invoice;
      const acc = data.account || {};
      setPreview({ business: data.business, sale: {
        invoice_no: inv.invoice_no, invoice_date: inv.invoice_date, created_at: inv.created_at,
        customer_name: inv.account_name, customer_phone: acc.phone || "", doctor_name: inv.doctor_name || "",
        items: inv.items, subtotal: inv.subtotal, gst_total: inv.gst_total,
        item_discount: inv.item_discount, overall_discount: inv.overall_discount,
        round_off: inv.round_off, total: inv.total, paid_amount: inv.total,
        payments: [], cashier: inv.created_by_name || "" } });
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <Loading />;
  const acc = data.account;
  const led = data.ledger;
  const openInvoice = data.open_invoice;

  return (
    <div data-testid="b2b-detail-page">
      <button onClick={onBack} className={`${ghostBtnCls} mb-4`} data-testid="b2b-back-btn">
        <ArrowLeft className="h-4 w-4" /> All accounts
      </button>
      <PageHeader overline={titleize(acc.account_type)} title={acc.name}
        subtitle={[acc.contact_person, acc.phone, acc.drug_licence && `DL ${acc.drug_licence}`].filter(Boolean).join(" · ")}
        actions={isAdmin && canManage && (
          <button onClick={async () => {
            const reason = window.prompt(`${(acc.status || "active") === "active" ? "Deactivate" : "Reactivate"} ${acc.name}? Reason:`);
            if (reason === null) return;
            try {
              await api.post(`/pharmacy/b2b/accounts/${accountId}/status`,
                { status: (acc.status || "active") === "active" ? "inactive" : "active", reason });
              toast.success("Account status updated."); load();
            } catch (e) { toast.error(apiError(e)); }
          }} className={ghostBtnCls} data-testid="b2b-toggle-status-btn">
            {(acc.status || "active") === "active" ? "Deactivate" : "Reactivate"}
          </button>
        )} />

      <div className="mb-6 grid gap-3 sm:grid-cols-4 rounded-lg bg-[#0F172A] p-5 text-white">
        {[["Invoiced", inr(led.invoiced), "b2b-led-invoiced"], ["Paid", inr(led.paid), "b2b-led-paid"],
          ["Credit limit", led.credit_limit ? inr(led.credit_limit) : "—", "b2b-led-limit"]].map(([k, v, t]) => (
          <div key={k}><p className="overline text-slate-400">{k}</p><p className="mt-1 font-semibold" data-testid={t}>{v}</p></div>
        ))}
        <div><p className="overline text-[#EA580C]">Outstanding</p>
          <p className="mt-1 font-ui text-xl font-bold" data-testid="b2b-led-outstanding">{inr(led.outstanding)}</p></div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`b2b-tab-${v}`}
            className={`rounded-full px-4 py-2 text-sm font-medium ${tab === v
              ? "bg-[#0F172A] text-white" : "border border-slate-300 text-slate-600 hover:border-slate-900"}`}>{l}</button>
        ))}
      </div>

      {tab === "today" && (
        <div className="space-y-6" data-testid="b2b-today-panel">
          <div className="panel p-5">
            <div className="mb-3 flex items-center justify-between">
              <p className="overline text-[#EA580C]">Today's open invoice ({today()})</p>
              {openInvoice && (
                <div className="flex gap-2">
                  {canManage && <button onClick={() => openEdit(openInvoice)} className={ghostBtnCls} data-testid="b2b-edit-open-btn">Edit</button>}
                  <button onClick={() => printInvoice(openInvoice.invoice_id)} className={ghostBtnCls} data-testid="b2b-print-open-btn"><Printer className="h-4 w-4" /> Print</button>
                  {canSettle && <button onClick={() => setCloseInv(openInvoice)} className={primaryBtnCls} data-testid="b2b-close-open-btn">Close & settle day</button>}
                </div>
              )}
            </div>
            {!openInvoice ? (
              <p className="text-sm text-slate-500" data-testid="b2b-no-open-invoice">No supply added today yet. Add items below to open today's invoice.</p>
            ) : (
              <div className="overflow-x-auto" data-testid="b2b-open-invoice">
                <p className="mb-2 text-sm"><b>{openInvoice.invoice_no}</b> · <span className="uppercase text-emerald-700">{openInvoice.status}</span></p>
                <LineTable items={openInvoice.items} />
                <div className="mt-3 flex justify-end"><InvTotals inv={openInvoice} /></div>
              </div>
            )}
          </div>

          {canManage && (
            <div className="panel p-5" data-testid="b2b-add-items-panel">
              <p className="overline text-[#EA580C] mb-4">Add supply to today's invoice</p>
              <div className="grid gap-4 sm:grid-cols-[1.6fr_0.6fr_0.7fr_0.6fr_auto] sm:items-end">
                <Field label="Product">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input className={`${inputCls} pl-9`} value={q} data-testid="b2b-product-search"
                      placeholder="Type code / name" onChange={(e) => search(e.target.value)} />
                  </div>
                </Field>
                <Field label="Quantity"><input type="number" min="1" className={inputCls} value={qty}
                  data-testid="b2b-qty-input" onChange={(e) => setQty(e.target.value)} /></Field>
                <Field label="Sell as">
                  <select className={inputCls} value={saleUnit} data-testid="b2b-unit-select"
                    onChange={(e) => setSaleUnit(e.target.value)}>
                    {(picked?.packaging || [{ code: "unit", label: "Unit" }]).map((u) => <option key={u.code} value={u.code}>{u.label}</option>)}
                  </select>
                </Field>
                <Field label="Disc %"><input type="number" min="0" className={inputCls} value={lineDisc}
                  data-testid="b2b-line-disc-input" onChange={(e) => setLineDisc(e.target.value)} /></Field>
                <button onClick={addToCart} className={primaryBtnCls} data-testid="b2b-add-to-cart-btn"><Plus className="h-4 w-4" /> Add</button>
              </div>
              {picked && (
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="b2b-picked">
                  <b>{picked.name}</b> · Batch {picked.batches?.[0]?.batch_no} · Rate {inr(picked.batches?.[0]?.selling_price || picked.batches?.[0]?.mrp)} · {picked.batches?.[0]?.quantity} base left
                </div>
              )}
              {!picked && results.length > 0 && (
                <div className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="b2b-search-results">
                  {results.map((m) => (
                    <button key={m.medicine_id} onClick={() => { setPicked(m); setSaleUnit(baseUnit(m)); }}
                      data-testid={`b2b-result-${m.medicine_id}`}
                      className="flex w-full items-center justify-between p-3 text-left text-sm hover:bg-slate-50">
                      <span className="font-medium text-slate-900">{m.name}</span>
                      <span className="text-slate-500">{m.batches?.length ? `${m.batches[0].quantity} left` : "No stock"}</span>
                    </button>
                  ))}
                </div>
              )}
              {cart.length > 0 && (
                <div className="mt-4">
                  <table className="w-full text-sm" data-testid="b2b-cart">
                    <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <tr><th className="py-2">Product</th><th className="py-2">Unit</th><th className="py-2 text-right">Qty</th>
                        <th className="py-2 text-right">Rate</th><th className="py-2 text-right">Disc%</th><th className="py-2" /></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {cart.map((l, i) => (
                        <tr key={i} data-testid={`b2b-cart-line-${i}`}>
                          <td className="py-2 font-medium">{l.name}</td><td className="py-2">{l.sale_unit}</td>
                          <td className="py-2 text-right">{l.quantity}</td><td className="py-2 text-right">{inr(l.rate)}</td>
                          <td className="py-2 text-right">{l.discount_percent}%</td>
                          <td className="py-2 text-right"><button onClick={() => setCart(cart.filter((_, x) => x !== i))}
                            className="text-red-600" data-testid={`b2b-cart-remove-${i}`}><Trash2 className="h-4 w-4" /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button onClick={addToInvoice} disabled={busy} className={`${primaryBtnCls} mt-4`} data-testid="b2b-add-to-invoice-btn">
                    Add to today's invoice
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "invoices" && (
        <Table testid="b2b-invoices-table" columns={["Invoice", "Date", "Status", "Lines", "Total", ""]}
          rows={data.invoices}
          empty={<EmptyState title="No invoices yet" testid="b2b-invoices-empty" />}
          renderRow={(inv) => (
            <tr key={inv.invoice_id} data-testid={`b2b-invoice-row-${inv.invoice_id}`}>
              <Td className="font-medium text-slate-900">{inv.invoice_no}</Td>
              <Td className="text-slate-600">{dmy(inv.invoice_date)}</Td>
              <Td><span className={`text-xs font-semibold uppercase ${inv.status === "open" ? "text-emerald-700" : inv.status === "closed" ? "text-slate-600" : "text-red-600"}`}>{inv.status}</span></Td>
              <Td className="text-slate-600">{inv.items?.length || 0}</Td>
              <Td className="font-medium">{inr(inv.total)}</Td>
              <Td><button onClick={() => printInvoice(inv.invoice_id)} className={ghostBtnCls} data-testid={`b2b-print-${inv.invoice_id}`}><Printer className="h-4 w-4" /> Print</button></Td>
            </tr>
          )} />
      )}

      {tab === "payments" && (
        <div className="space-y-5" data-testid="b2b-payments-panel">
          {canSettle && <button onClick={() => setPayOpen(true)} className={primaryBtnCls} data-testid="b2b-record-payment-btn">Record settlement</button>}
          <Table testid="b2b-payments-table" columns={["Receipt", "Date", "Mode", "Reference", "Amount"]}
            rows={data.payments}
            empty={<EmptyState title="No settlements recorded" testid="b2b-payments-empty" />}
            renderRow={(p) => (
              <tr key={p.payment_id} data-testid={`b2b-payment-row-${p.payment_id}`}>
                <Td className="font-medium text-slate-900">{p.payment_no}</Td>
                <Td className="text-slate-600">{dmy(p.paid_on || p.created_at)}</Td>
                <Td className="text-slate-600">{titleize(p.mode)}</Td>
                <Td className="text-slate-500">{p.reference || "—"}</Td>
                <Td className="font-medium">{inr(p.amount)}</Td>
              </tr>
            )} />
        </div>
      )}

      {tab === "statement" && (
        <div className="space-y-5" data-testid="b2b-statement-panel">
          <div className="flex items-end gap-3">
            <Field label="Month"><input type="month" className={inputCls} value={month}
              data-testid="b2b-month-input" onChange={(e) => setMonth(e.target.value)} /></Field>
            <button onClick={loadStatement} className={ghostBtnCls} data-testid="b2b-statement-load-btn">View</button>
          </div>
          {statement && (
            <>
              <div className="grid gap-3 sm:grid-cols-4 rounded-lg border border-slate-200 p-5">
                {[["Opening", statement.opening_balance], ["Invoiced", statement.invoiced],
                  ["Paid", statement.paid], ["Closing", statement.closing_balance]].map(([k, v]) => (
                  <div key={k}><p className="overline text-slate-400">{k}</p><p className="mt-1 font-semibold" data-testid={`b2b-stmt-${k.toLowerCase()}`}>{inr(v)}</p></div>
                ))}
              </div>
              <Table testid="b2b-statement-invoices" columns={["Invoice", "Date", "Status", "Total"]}
                rows={statement.invoices} empty={<EmptyState title="No invoices this month" testid="b2b-stmt-empty" />}
                renderRow={(inv) => (
                  <tr key={inv.invoice_id}><Td className="font-medium">{inv.invoice_no}</Td>
                    <Td className="text-slate-600">{dmy(inv.invoice_date)}</Td>
                    <Td className="text-slate-600">{inv.status}</Td><Td>{inr(inv.total)}</Td></tr>
                )} />
            </>
          )}
        </div>
      )}

      {tab === "items" && (
        <Table testid="b2b-items-table" columns={["Invoice", "Date", "Product", "Batch", "Unit", "Qty", "Total"]}
          rows={itemHistory?.items || []}
          empty={<EmptyState title="No item history" testid="b2b-items-empty" />}
          renderRow={(r, i) => (
            <tr key={i} data-testid={`b2b-item-row-${i}`}>
              <Td className="font-medium text-slate-900">{r.invoice_no}</Td>
              <Td className="text-slate-600">{dmy(r.invoice_date)}</Td>
              <Td className="text-slate-800">{r.medicine_name}</Td>
              <Td className="text-slate-500">{r.batch_no}</Td>
              <Td className="text-slate-600">{r.sale_unit}</Td>
              <Td className="text-slate-600">{r.quantity}</Td>
              <Td className="font-medium">{inr(r.line_total)}</Td>
            </tr>
          )} />
      )}

      <Modal open={Boolean(editInv)} onClose={() => setEditInv(null)} wide title="Edit open daily invoice"
        subtitle={editInv?.invoice_no} testid="b2b-edit-modal">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Only open daily invoices can be edited. Stock is restored and re-deducted; the change is audited.</p>
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="py-2">Product</th><th className="py-2 text-right">Qty ({"unit"})</th><th className="py-2 text-right">Disc%</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {editLines.map((l, i) => (
                <tr key={i} data-testid={`b2b-edit-line-${i}`}>
                  <td className="py-2 font-medium">{l.name}<span className="block text-xs text-slate-400">{l.batch_no} · {l.sale_unit}</span></td>
                  <td className="py-2 text-right"><input type="number" min="0" className={`${inputCls} !w-24`} value={l.quantity}
                    data-testid={`b2b-edit-qty-${i}`} onChange={(e) => setEditLines(editLines.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} /></td>
                  <td className="py-2 text-right"><input type="number" min="0" className={`${inputCls} !w-20`} value={l.discount_percent}
                    onChange={(e) => setEditLines(editLines.map((x, j) => j === i ? { ...x, discount_percent: e.target.value } : x))} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Field label="Reason for correction *">
            <input className={inputCls} value={editReason} data-testid="b2b-edit-reason"
              onChange={(e) => setEditReason(e.target.value)} placeholder="e.g. wrong quantity entered" />
          </Field>
          <button onClick={submitEdit} className={primaryBtnCls} data-testid="b2b-edit-submit-btn">Save correction</button>
        </div>
      </Modal>

      <Modal open={Boolean(closeInv)} onClose={() => setCloseInv(null)} title="Close & settle the day"
        subtitle={closeInv?.invoice_no} testid="b2b-close-modal">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">This finalises today's invoice ({inr(closeInv?.total || 0)}). No more items can be added or edited afterwards; the amount rolls into the account's outstanding balance.</p>
          <button onClick={doClose} className={primaryBtnCls} data-testid="b2b-close-submit-btn">Close daily invoice</button>
        </div>
      </Modal>

      <Modal open={payOpen} onClose={() => setPayOpen(false)} title="Record settlement" subtitle={acc.name} testid="b2b-payment-modal">
        <form onSubmit={recordPayment} className="grid gap-5 sm:grid-cols-2">
          <Field label="Amount (₹) *"><input required type="number" min="1" step="0.01" className={inputCls}
            value={payment.amount} data-testid="b2b-payment-amount" onChange={(e) => setPayment({ ...payment, amount: e.target.value })} /></Field>
          <Field label="Mode">
            <select className={inputCls} value={payment.mode} data-testid="b2b-payment-mode"
              onChange={(e) => setPayment({ ...payment, mode: e.target.value })}>
              {["bank_transfer", "cash", "upi", "cheque", "card", "other"].map((m) => <option key={m} value={m}>{titleize(m)}</option>)}
            </select>
          </Field>
          <Field label="Reference"><input className={inputCls} value={payment.reference}
            onChange={(e) => setPayment({ ...payment, reference: e.target.value })} /></Field>
          <Field label="Paid on"><input type="date" className={inputCls} value={payment.paid_on}
            onChange={(e) => setPayment({ ...payment, paid_on: e.target.value })} /></Field>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="b2b-payment-submit-btn">Save settlement</button></div>
        </form>
      </Modal>

      <PrintPreview open={Boolean(preview)} onClose={() => setPreview(null)}
        title={`Invoice ${preview?.sale?.invoice_no || ""}`} subtitle="B2B daily invoice" testid="b2b-invoice-preview">
        <PharmacyInvoiceDoc sale={preview?.sale} business={preview?.business} template="classic" />
      </PrintPreview>
    </div>
  );
}

function LineTable({ items }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
        <tr><th className="py-2">Product</th><th className="py-2">Batch</th><th className="py-2 text-right">Qty</th>
          <th className="py-2 text-right">Rate</th><th className="py-2 text-right">Disc</th><th className="py-2 text-right">GST</th><th className="py-2 text-right">Total</th></tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {items.map((l, i) => (
          <tr key={i}>
            <td className="py-2 font-medium">{l.medicine_name}</td>
            <td className="py-2 text-slate-500">{l.batch_no}</td>
            <td className="py-2 text-right">{l.quantity} {l.sale_unit}</td>
            <td className="py-2 text-right">{inr(l.rate)}</td>
            <td className="py-2 text-right">{inr(l.discount || 0)}</td>
            <td className="py-2 text-right">{l.gst_percent}%</td>
            <td className="py-2 text-right font-medium">{inr(l.line_total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InvTotals({ inv }) {
  return (
    <div className="w-64 space-y-1 text-sm">
      <div className="flex justify-between"><span className="text-slate-600">Taxable</span><span>{inr(inv.subtotal)}</span></div>
      <div className="flex justify-between"><span className="text-slate-600">GST</span><span>{inr(inv.gst_total)}</span></div>
      <div className="flex justify-between"><span className="text-slate-600">Round off</span><span>{inr(inv.round_off || 0)}</span></div>
      <div className="mt-1 flex justify-between border-t-2 border-slate-900 pt-2 font-ui font-bold">
        <span>Total</span><span data-testid="b2b-invoice-total">{inr(inv.total)}</span></div>
    </div>
  );
}
